// =============================================================================
// SIREPPAR - Wizard de Carga de Candidato (Candidaturas Gaiman 2026)
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { candidatoService } from '../../../services/api';
import Paso1DatosPersonales from './Paso1DatosPersonales';
import Paso2Domicilio from './Paso2Domicilio';
import Paso3FotoBoleta from './Paso3FotoBoleta';
import Paso4Documentacion from './Paso4Documentacion';
import Paso5DeclaracionJurada from './Paso5DeclaracionJurada';
import Paso6RevisionEnvio from './Paso6RevisionEnvio';

export interface Documento {
    id: string;
    tipo: string;
    nombreOriginal: string;
    tamanioBytes: number;
    urlDescarga: string;
    creadoEn: string;
  }  

export interface Candidato {
  id: string;
  partidoId: string;
  convocatoriaId: string;
  dni: string | null;
  apellido: string | null;
  nombre: string | null;
  fechaNacimiento: string | null;
  genero: string | null;
  nacionalidad: string | null;
  domicilioCalle: string | null;
  domicilioNumero: string | null;
  domicilioPisoDepto: string | null;
  localidad: string | null;
  codigoPostal: string | null;
  anosResidenciaDeclarada: number | null;
  mesesResidenciaDeclarada: number | null;
  apodo: string | null;
  nombreEnBoleta: string | null;
  observaciones: string | null;
  declaracionArt72: { incisos?: Record<string, boolean>; candidaturaUnica?: boolean; declaracionFinal?: boolean } | null;
  cargo: string;
  estado: string;
  telefono: string | null;
  email: string | null;
  documentos: Documento[];
  partido: { denominacion: string; sigla: string | null; tipo: string } | null;
}

const PASOS = [
  { n: 1, label: 'Datos\npersonales' },
  { n: 2, label: 'Domicilio\ny residencia' },
  { n: 3, label: 'Foto y datos\nde boleta' },
  { n: 4, label: 'Documentación\nArt. 77' },
  { n: 5, label: 'Declaración\njurada' },
  { n: 6, label: 'Revisión\ny envío' },
];

export default function CandidaturaWizardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [paso, setPaso] = useState(1);
  const [visitados, setVisitados] = useState<Set<number>>(new Set([1]));
  const [inicializado, setInicializado] = useState(false);

  const { data: candidato, isLoading, error } = useQuery<Candidato>({
    queryKey: ['candidato', id],
    queryFn: async () => (await candidatoService.obtener(id!)).data.data,
    enabled: !!id,
  });

  useEffect(() => {
    if (!candidato || inicializado) return;

    let cancelado = false;

    (async () => {
      // El DNI es el único paso con validación externa (padrón). Si el DNI guardado
      // no se encontró de verdad, no se puede dar por completo el paso 1 — ni acá
      // ni en el botón "Continuar" del propio paso.
      let dniValido = false;
      if (candidato.dni) {
        try {
          const res = await candidatoService.verificarDni(candidato.dni);
          dniValido = Boolean(res.data.data?.enPadron);
        } catch {
          dniValido = false;
        }
      }

      if (cancelado) return;

      const v = new Set([1]);
      if (dniValido) v.add(2);
      if (candidato.domicilioCalle) v.add(3);
      if (candidato.nombreEnBoleta) v.add(4);
      if (candidato.declaracionArt72?.declaracionFinal) v.add(5);

      setVisitados(v);
      setPaso(Math.max(...v));
      setInicializado(true);
    })();

    return () => { cancelado = true; };
  }, [candidato, inicializado]);

  const irAPaso = (n: number) => {
    if (visitados.has(n)) setPaso(n);
  };

  const avanzarA = (n: number) => {
    setVisitados(prev => new Set(prev).add(n));
    setPaso(n);
  };

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['candidato', id] });

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Error al cargar el candidato</p>
      </div>
    );
  }
  if (isLoading || !candidato) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">🏛️</div>
            <div>
              <h1 className="font-bold text-gray-900">Carga de candidato — {candidato.cargo}</h1>
              <p className="text-sm text-gray-500">Paso {paso} de 6</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Borrador — se guarda automáticamente
            </span>
            <button onClick={() => navigate('/apoderado/panel')} className="text-gray-500 hover:text-gray-700 text-sm">← Volver al panel</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Barra de progreso */}
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <div className="flex items-center">
            {PASOS.map((p, i) => {
              const estadoVisual = p.n === paso ? 'active' : visitados.has(p.n) ? 'done' : 'pending';
              return (
                <div key={p.n} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => irAPaso(p.n)}
                      disabled={!visitados.has(p.n)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        estadoVisual === 'active' ? 'bg-blue-600 text-white' :
                        estadoVisual === 'done' ? 'bg-green-600 text-white cursor-pointer' :
                        'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {estadoVisual === 'done' ? '✓' : p.n}
                    </button>
                    <div className={`mt-1.5 text-[11px] text-center whitespace-pre-line leading-tight ${estadoVisual === 'active' ? 'text-blue-700 font-semibold' : 'text-gray-500'}`}>
                      {p.label}
                    </div>
                  </div>
                  {i < PASOS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 mb-5 ${visitados.has(p.n + 1) ? 'bg-green-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {paso === 1 && (
          <Paso1DatosPersonales candidato={candidato} onSaved={invalidar} onNext={() => avanzarA(2)} />
        )}
        {paso === 2 && (
          <Paso2Domicilio candidato={candidato} onSaved={invalidar} onBack={() => setPaso(1)} onNext={() => avanzarA(3)} />
        )}
        {paso === 3 && (
          <Paso3FotoBoleta candidato={candidato} onSaved={invalidar} onBack={() => setPaso(2)} onNext={() => avanzarA(4)} />
        )}
        {paso === 4 && (
          <Paso4Documentacion candidato={candidato} onSaved={invalidar} onBack={() => setPaso(3)} onNext={() => avanzarA(5)} />
        )}
        {paso === 5 && (
          <Paso5DeclaracionJurada candidato={candidato} onSaved={invalidar} onBack={() => setPaso(4)} onNext={() => avanzarA(6)} />
        )}
        {paso === 6 && (
          <Paso6RevisionEnvio candidato={candidato} onSaved={invalidar} onBack={() => setPaso(5)} onEditar={irAPaso} />
        )}
      </main>
    </div>
  );
}
